import React, { useContext, useEffect, useState } from "react";

import PersonalDetailsForm, {
  PersonalDetailsFormValues,
} from "~/modules/checkout/components/PersonalDetailsForm/PersonalDetailsForm";
import OrderContext from "~/contexts/OrderContext";
import { useFetcher, useLocation } from "@remix-run/react";
import SiteContext from "~/contexts/SiteContext";
import { AuthMethod, BaseUser } from "~/entities/authEntities";
import AuthForm from "~/modules/auth/containers/AuthForm/AuthForm";
import AuthContext from "~/contexts/AuthContext";
import { getServiceSettings } from "~/helpers/siteHelpers";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import { LABELS } from "~/modules/checkout/constants";

import styles from "./AuthDialog.module.css";
import Dialog from "~/components/Dialog/Dialog";
import { formatDateValue } from "~/helpers/dateHelper";

interface AuthDialogProps {
  visible: boolean;
  onClose: () => void;
  initialData?: BaseUser;
}

const AuthDialog: React.FC<AuthDialogProps> = ({
  visible,
  onClose,
  initialData,
}) => {
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null);
  const [comoSignUpLoading, setComoSignUpLoading] = useState(false);
  const [disabledCloseDialog, setDisabledCloseDialog] = useState(false);

  const location = useLocation();
  const isCheckoutPage = location.pathname.split("?")[0].endsWith("/checkout");
  const fetcher = useFetcher({});
  const { orderSummary, setCustomerData, signBackInRequired } =
    useContext(OrderContext);
  const { orgId, site } = useContext(SiteContext);
  const { isAuthEnabled, requiresSignup, useComoAuth } =
    useContext(AuthContext);

  const serviceSettings = getServiceSettings(site, orderSummary.orderTypeCode);
  const allowGuestCheckout =
    serviceSettings?.settings.allowGuestCheckout &&
    isCheckoutPage &&
    !signBackInRequired;

  let title;
  if (requiresSignup) {
    title = LABELS.SIGNUP;
  } else if (isAuthEnabled && !authMethod && allowGuestCheckout) {
    title = LABELS.LOGIN_OR_GUEST;
  } else if (!allowGuestCheckout) {
    title = LABELS.LOGIN;
  } else if (authMethod && authMethod !== AuthMethod.GUEST) {
    title = LABELS.REQUEST_CODE;
  } else {
    title = LABELS.PERSONAL_DETAILS;
  }

  const handleCancelSignup = () => {
    const formData = new FormData();
    formData.append("orgId", orgId);
    formData.append("siteId", site?.id ?? "");
    formData.append("orderId", orderSummary.id);
    formData.append("useComo", `${useComoAuth}`);
    if (orderSummary && orderSummary.id) {
      formData.append("orderSummary", JSON.stringify(orderSummary));
    }
    fetcher.submit(formData, {
      method: "POST",
      action: "/auth/logout",
    });
    handleClose();
  };

  const handleClose = () => {
    setAuthMethod(null);
    onClose();
  };

  useEffect(() => {
    if (fetcher?.data && comoSignUpLoading) {
      setComoSignUpLoading(false);
    }
  }, [comoSignUpLoading, fetcher?.data]);

  const handleSubmit = async (
    values: PersonalDetailsFormValues,
    allowUpdateCustomerDetails: boolean,
  ) => {
    if (requiresSignup) {
      const formData = new FormData();
      formData.append("siteId", site.id);
      formData.append("orderId", orderSummary.id);
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      if (values.birthday) {
        formData.append("birthday", formatDateValue(new Date(values.birthday)));
      }
      formData.append("termsOfUse", values.termsOfUse.toString());
      formData.append("joinLoyalty", values.joinLoyalty.toString());

      if (useComoAuth) {
        setComoSignUpLoading(true);
        formData.append("useComo", useComoAuth.toString());
      }

      fetcher.submit(formData, {
        method: "POST",
        action: "/auth/signup",
      });
    } else {
      setCustomerData(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
        },
        allowUpdateCustomerDetails,
      );
    }

    handleClose();
  };

  const renderAuthContent = () => {
    if (isAuthEnabled && !requiresSignup && authMethod != AuthMethod.GUEST) {
      return (
        <AuthForm
          allowGuestCheckout={!!allowGuestCheckout}
          setAuthMethod={setAuthMethod}
          onComplete={handleClose}
          setDisabledCloseDialog={setDisabledCloseDialog}
        />
      );
    }

    return (
      <PersonalDetailsForm
        loading={comoSignUpLoading || fetcher.state !== "idle"}
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={requiresSignup ? handleCancelSignup : handleClose}
        isSignup={requiresSignup}
      />
    );
  };

  useEffect(() => {
    if (!visible) {
      setDisabledCloseDialog(false);
    }
  }, [visible]);

  return (
    <Dialog
      visible={visible}
      onClose={() => {
        if (disabledCloseDialog) return;

        if (requiresSignup) {
          handleCancelSignup();
        } else {
          handleClose();
        }
      }}
    >
      <div className={styles.authDialogContent}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <CircleCloseButton
            color="#828282"
            onClick={requiresSignup ? handleCancelSignup : handleClose}
          />
        </div>
        {renderAuthContent()}
      </div>
    </Dialog>
  );
};

export default AuthDialog;
